import {IssueRelativeTime} from '@github-ui/ago'
import {useHoverPopover} from '@github-ui/use-hover-popover'
import {AnchoredOverlay, Link, VisuallyHidden} from '@primer/react'
import {
  type FocusEventHandler,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  useCallback,
  useRef,
} from 'react'

import {getArtifactLinkDisplayName, getSafeArtifactHref} from '../artifact-url'
import styles from './ArtifactLink.module.css'

export type ArtifactLinkActor = {
  login: string
}

export type ArtifactLinkProps = {
  name: string
  url: string
  updatedAt?: string
  updatedByActor?: ArtifactLinkActor
  className?: string
  textClassName?: string
  popoverAnchorRef?: RefObject<HTMLElement | null>
  onClick?: MouseEventHandler<HTMLAnchorElement>
  onAuxClick?: MouseEventHandler<HTMLAnchorElement>
  onFocus?: FocusEventHandler<HTMLAnchorElement>
  onBlur?: FocusEventHandler<HTMLAnchorElement>
  renderFallback?: (displayName: string) => ReactNode
}

export function ArtifactLink({
  name,
  url,
  updatedAt,
  updatedByActor,
  className,
  textClassName,
  popoverAnchorRef,
  onClick,
  onAuxClick,
  onFocus,
  onBlur,
  renderFallback,
}: ArtifactLinkProps) {
  const linkRef = useRef<HTMLAnchorElement>(null)
  const {open, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover<HTMLSpanElement>()
  const displayName = getArtifactLinkDisplayName(name, url)
  const href = getSafeArtifactHref(url)

  const handleFocus = useCallback<FocusEventHandler<HTMLAnchorElement>>(
    event => {
      anchorProps.onFocus()
      onFocus?.(event)
    },
    [anchorProps, onFocus],
  )
  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    event => {
      anchorProps.onClick()
      onClick?.(event)
    },
    [anchorProps, onClick],
  )
  const handleBlur = useCallback<FocusEventHandler<HTMLAnchorElement>>(
    event => {
      anchorProps.onBlur(event)
      onBlur?.(event)
    },
    [anchorProps, onBlur],
  )

  if (!href) {
    return renderFallback?.(displayName) ?? <span className={textClassName}>{displayName}</span>
  }

  return (
    <span className={styles.AnchorRegion} {...anchorRegionProps}>
      <Link
        {...anchorProps}
        ref={linkRef}
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onAuxClick={onAuxClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <span className={textClassName}>{displayName}</span>
        <VisuallyHidden>
          <span>Full URL: {url}.</span>
          {updatedAt ? (
            <span>
              {' '}
              Updated on <time dateTime={updatedAt}>{updatedAt}</time>
              {updatedByActor ? <> by {updatedByActor.login}.</> : <>.</>}
            </span>
          ) : null}
        </VisuallyHidden>
      </Link>
      {open ? (
        <AnchoredOverlay
          open={open}
          anchorRef={popoverAnchorRef ?? linkRef}
          renderAnchor={null}
          onClose={closeNow}
          side="outside-bottom"
          align="start"
          focusTrapSettings={{disabled: true}}
          focusZoneSettings={{disabled: true}}
          overlayProps={{preventFocusOnOpen: true, returnFocusRef: linkRef}}
        >
          <div {...contentProps} aria-hidden="true" className={styles.LinkPopover} data-testid="link-artifact-popover">
            <span className={styles.LinkPopoverUrl}>{url}</span>
            {updatedAt ? (
              <span className={styles.LinkPopoverMetadata}>
                Updated <IssueRelativeTime datetime={updatedAt} />
                {updatedByActor ? <> by {updatedByActor.login}</> : null}
              </span>
            ) : null}
          </div>
        </AnchoredOverlay>
      ) : null}
    </span>
  )
}
